import { createContext, useContext } from 'react'
import { FlightsClassTypes, type FareClassWithReward } from 'constants/flight'
import noop from 'lib/function/noop'

interface MaldivesDirectBundleContextValue {
  charterBundle: boolean
  isPricePerPerson: boolean
  fareClass: FlightsClassTypes
  /** Full fare class including reward variant (e.g. economy-rewards). */
  selectedFareClass?: FareClassWithReward
  departureAirportCode?: string
  departureAirportName?: string
  duration?: number
  allowedFareClassesByOfferId: Map<string, Array<FlightsClassTypes>>
  hasFareClassRestrictionsByOfferId: Map<string, boolean>
  /** True when the user has explicitly selected dates (not just the cheapest-date fallback). */
  hasUserSelectedDates: boolean
  /**
   * True only when the main flight search has resolved AND at least one leg carries a
   * positive `feederEstimatedPrice` for the picked dates. Undefined when no dates have
   * been selected yet (consumers should treat undefined as "not evaluated" and fall
   * back to the configured-feeder-airport gate alone).
   */
  hasFeederFlightPriced?: boolean
  /** True when selected charter dates have a free MLE to MEL return economy fare. */
  hasReturnFareFree?: boolean
  /**
   * Société Rewards / Spend Points: show indicative Classic Rewards pricing from package
   * hotel totals + hardcoded preview rates (no stream / live reward availability).
   */
  useRewardPreviewPricing?: boolean
  canOpenFareClassModal: boolean
  openFareClassModal: () => void
  selectedCabin?: FlightsClassTypes | null
  rewardSeatsOnly?: boolean
  isPromotionPriceApplied?: boolean
}

export const EMPTY_FARE_CLASS_MAP = new Map<string, Array<FlightsClassTypes>>()
export const EMPTY_RESTRICTIONS_MAP = new Map<string, boolean>()

const MaldivesDirectBundleContext = createContext<MaldivesDirectBundleContextValue>({
  charterBundle: false,
  isPricePerPerson: true,
  fareClass: FlightsClassTypes.ECONOMY,
  departureAirportCode: undefined,
  departureAirportName: undefined,
  duration: undefined,
  allowedFareClassesByOfferId: EMPTY_FARE_CLASS_MAP,
  hasFareClassRestrictionsByOfferId: EMPTY_RESTRICTIONS_MAP,
  hasUserSelectedDates: false,
  canOpenFareClassModal: false,
  openFareClassModal: noop,
})

export const MaldivesDirectBundleProvider = MaldivesDirectBundleContext.Provider

export function useMaldivesDirectBundleContext(): MaldivesDirectBundleContextValue {
  return useContext(MaldivesDirectBundleContext)
}
